import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { ChevronLeft, Clock } from "lucide-react";
import { api } from "../lib/api";
import { usePageTitle } from "../lib/usePageTitle";
import { ShareButtons } from "../components/ShareButtons";
import { JobApplyForm } from "../components/JobApplyForm";

const fmtDate = (iso) => new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });

function postJsonLd(post) {
  const url = `https://stuffcbd.fr/blog/${post.slug}`;
  const org = { "@type": "Organization", name: "STUFF CBD", url: "https://stuffcbd.fr", logo: "https://stuffcbd.fr/stuff-logo.png" };
  if (post.job_posting) {
    const jp = post.job_posting;
    return {
      "@context": "https://schema.org", "@type": "JobPosting",
      title: jp.title || post.title, description: post.excerpt, datePosted: (post.created_at || "").slice(0, 10),
      employmentType: jp.employmentType || ["PART_TIME", "CONTRACTOR"],
      hiringOrganization: { ...org, name: jp.hiringOrganization || "STUFF" },
      jobLocation: { "@type": "Place", address: { "@type": "PostalAddress", addressLocality: jp.city, addressCountry: "FR" } },
      directApply: true, url,
    };
  }
  return {
    "@context": "https://schema.org", "@type": "Article",
    headline: post.title, description: post.excerpt, image: post.image_url || undefined,
    datePublished: post.created_at, author: org, publisher: org, mainEntityOfPage: url, articleSection: post.category,
  };
}

function useJsonLd(post) {
  useEffect(() => {
    if (!post) return undefined;
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-testid", "blog-jsonld");
    el.text = JSON.stringify(postJsonLd(post));
    document.head.appendChild(el);
    return () => el.remove();
  }, [post]);
}

const CategoryBadge = ({ cat }) => (
  <Link to={`/blog?cat=${encodeURIComponent(cat)}`} data-testid="blog-category-badge"
    className="inline-block bg-neon-magenta/15 border border-neon-magenta/50 text-neon-magenta text-[11px] uppercase font-bold px-2 py-0.5 hover:bg-neon-magenta hover:text-black transition-colors">
    {cat}
  </Link>
);

const TagBadge = ({ tag }) => (
  <Link to={`/blog?tag=${encodeURIComponent(tag)}`} data-testid="blog-tag-badge"
    className="inline-block border border-zinc-700 text-zinc-400 text-[11px] px-2 py-0.5 hover:border-neon-green hover:text-neon-green transition-colors">
    #{tag}
  </Link>
);

// ---- Mini renderer markdown ----
const renderInline = (text) => {
  const parts = text.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\)|\*[^*]+\*)/g);
  return parts.map((part, i) => {
    if (!part) return null;
    if (part.startsWith("**") && part.endsWith("**")) return <strong key={`${i}-${part}`} className="text-white font-semibold">{part.slice(2, -2)}</strong>;
    const link = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (link) {
      const [, label, url] = link;
      if (url.startsWith("/")) return <Link key={`${i}-${url}`} to={url} className="text-neon-green underline underline-offset-2 hover:text-white transition-colors">{label}</Link>;
      return <a key={`${i}-${url}`} href={url} target="_blank" rel="noopener noreferrer" className="text-neon-green underline underline-offset-2 hover:text-white transition-colors">{label}</a>;
    }
    if (part.startsWith("*") && part.endsWith("*") && part.length > 2) return <em key={`${i}-${part}`} className="text-zinc-400">{part.slice(1, -1)}</em>;
    return part;
  });
};

const parseBlocks = (content) => {
  const lines = content.split("\n");
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) { i++; continue; }
    if (line.startsWith("## ")) { blocks.push({ type: "h2", text: line.slice(3) }); i++; continue; }
    const img = line.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
    if (img) { blocks.push({ type: "img", alt: img[1], src: img[2] }); i++; continue; }
    if (line.startsWith("|")) {
      const rows = [];
      while (i < lines.length && lines[i].startsWith("|")) {
        if (!/^\|[\s\-|:]+\|$/.test(lines[i])) rows.push(lines[i].split("|").slice(1, -1).map((c) => c.trim()));
        i++;
      }
      blocks.push({ type: "table", rows });
      continue;
    }
    if (line.startsWith("- ")) {
      const items = [];
      while (i < lines.length && lines[i].startsWith("- ")) { items.push(lines[i].slice(2)); i++; }
      blocks.push({ type: "ul", items });
      continue;
    }
    if (/^\d+\.\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i])) { items.push(lines[i].replace(/^\d+\.\s/, "")); i++; }
      blocks.push({ type: "ol", items });
      continue;
    }
    if (line.startsWith("*⚠️")) { blocks.push({ type: "disclaimer", text: line.replace(/^\*|\*$/g, "").replace(/\*\s*\*/g, " ") }); i++; continue; }
    blocks.push({ type: "p", text: line });
    i++;
  }
  return blocks;
};

const BlockRenderer = ({ content }) => {
  const blocks = useMemo(() => parseBlocks(content), [content]);
  return (
    <div className="text-zinc-300 leading-relaxed space-y-5">
      {blocks.map((b, i) => {
        if (b.type === "h2") return <h2 key={`${b.type}-${i}`} className="font-anton text-2xl uppercase text-neon-green pt-4">{b.text}</h2>;
        if (b.type === "img") return <img key={`${b.type}-${i}`} src={b.src} alt={b.alt} loading="lazy" className="w-full border border-zinc-800 my-2" data-testid="blog-content-image" />;
        if (b.type === "ul") return (
          <ul key={`${b.type}-${i}`} className="space-y-2">
            {b.items.map((it, j) => <li key={j} className="pl-4 border-l-2 border-neon-magenta/40">{renderInline(it)}</li>)}
          </ul>
        );
        if (b.type === "ol") return (
          <ol key={`${b.type}-${i}`} className="space-y-2 list-none">
            {b.items.map((it, j) => (
              <li key={j} className="flex gap-3"><span className="font-anton text-neon-green shrink-0">{j + 1}.</span><span>{renderInline(it)}</span></li>
            ))}
          </ol>
        );
        if (b.type === "table") return (
          <div key={`${b.type}-${i}`} className="overflow-x-auto">
            <table className="w-full text-sm border border-zinc-800">
              <thead>
                <tr className="bg-ink-surface">{b.rows[0]?.map((c, j) => <th key={j} className="text-left font-anton uppercase text-neon-green px-3 py-2 border-b border-zinc-800">{c}</th>)}</tr>
              </thead>
              <tbody>
                {b.rows.slice(1).map((row, j) => (
                  <tr key={j} className="border-b border-zinc-800/60">{row.map((c, k) => <td key={k} className={`px-3 py-2 ${k === 0 ? "text-white font-semibold" : ""}`}>{c}</td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>
        );
        if (b.type === "disclaimer") return (
          <p key={`${b.type}-${i}`} data-testid="blog-disclaimer" className="text-zinc-500 text-xs italic border border-zinc-800 bg-ink-surface p-4 !mt-8">{b.text}</p>
        );
        return <p key={`${b.type}-${i}`}>{renderInline(b.text)}</p>;
      })}
    </div>
  );
};

export function BlogList() {
  usePageTitle("Blog CBD — Conseils, légalité, guides | STUFF CBD",
    "Tout savoir sur le CBD : légalité en France, produits, recrutement vendeurs, tendances. Les guides STUFF CBD.");
  const [posts, setPosts] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const cat = searchParams.get("cat");
  const tag = searchParams.get("tag");
  useEffect(() => { api.get("/blog").then((r) => setPosts(r.data)).catch(() => setPosts([])); }, []);

  const categories = useMemo(() => [...new Set((posts || []).map((p) => p.category).filter(Boolean))], [posts]);
  const filtered = useMemo(() => (posts || []).filter((p) =>
    (!cat || p.category === cat) && (!tag || (p.tags || []).includes(tag))), [posts, cat, tag]);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12" data-testid="blog-list">
      <p className="font-graffiti text-neon-magenta text-2xl">Le mag</p>
      <h1 className="font-anton text-4xl sm:text-5xl uppercase text-white mb-2">Blog <span className="neon-text-green">STUFF</span></h1>
      <p className="text-zinc-500 mb-8">Conseils, guides et actus CBD — pour consommer malin.</p>

      {categories.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-8" data-testid="blog-filters">
          <button onClick={() => setSearchParams({})} data-testid="blog-filter-all"
            className={`text-xs uppercase font-bold px-3 py-1.5 border transition-colors ${!cat && !tag ? "bg-neon-green text-black border-neon-green" : "border-zinc-700 text-zinc-400 hover:border-neon-green hover:text-neon-green"}`}>
            Tous
          </button>
          {categories.map((c) => (
            <button key={c} onClick={() => setSearchParams({ cat: c })} data-testid={`blog-filter-${c}`}
              className={`text-xs uppercase font-bold px-3 py-1.5 border transition-colors ${cat === c ? "bg-neon-green text-black border-neon-green" : "border-zinc-700 text-zinc-400 hover:border-neon-green hover:text-neon-green"}`}>
              {c}
            </button>
          ))}
        </div>
      )}
      {tag && (
        <p className="text-zinc-400 text-sm mb-6">Filtre : <span className="text-neon-green">#{tag}</span> — <button onClick={() => setSearchParams({})} className="underline text-zinc-500 hover:text-white">retirer</button></p>
      )}

      {!posts ? <p className="text-zinc-500">Chargement...</p> : filtered.length === 0 ? <p className="text-zinc-500">Aucun article pour le moment.</p> : (
        <div className="grid sm:grid-cols-2 gap-5">
          {filtered.map((p) => (
            <Link key={p.id} to={`/blog/${p.slug}`} data-testid="blog-card" className="block bg-ink-surface border border-zinc-800 hover:border-neon-green transition-all group overflow-hidden">
              {p.image_url && (
                <div className="aspect-[16/9] overflow-hidden bg-black">
                  <img src={p.image_url} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
              )}
              <div className="p-5">
                <div className="flex items-center gap-2 mb-2">
                  {p.category && <span className="bg-neon-magenta/15 border border-neon-magenta/50 text-neon-magenta text-[11px] uppercase font-bold px-2 py-0.5">{p.category}</span>}
                  {p.reading_minutes && <span className="text-zinc-600 text-[11px] flex items-center gap-1"><Clock className="h-3 w-3" />{p.reading_minutes} min</span>}
                </div>
                <h2 className="font-anton text-lg sm:text-xl uppercase text-white leading-tight group-hover:text-neon-green transition-colors">{p.title}</h2>
                <p className="text-zinc-500 text-xs mt-1">{fmtDate(p.created_at)}</p>
                <p className="text-zinc-400 text-sm mt-2 line-clamp-3">{p.excerpt}</p>
                <span className="text-neon-green text-xs uppercase font-bold mt-3 inline-block">Lire l'article →</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);
  useEffect(() => {
    setPost(null); setNotFound(false);
    api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setNotFound(true));
    window.scrollTo(0, 0);
  }, [slug]);
  usePageTitle(post ? (post.meta_title || `${post.title} — Blog STUFF CBD`) : undefined, post?.excerpt);
  useJsonLd(post);

  if (notFound) return (
    <div className="max-w-3xl mx-auto px-4 py-20 text-center">
      <h1 className="font-anton text-3xl uppercase text-white mb-4">Article introuvable</h1>
      <Link to="/blog" className="text-neon-green uppercase font-semibold">← Retour au blog</Link>
    </div>
  );
  if (!post) return <p className="text-zinc-500 text-center py-20">Chargement...</p>;

  const hasContentImage = /!\[[^\]]*\]\([^)]+\)/.test(post.content);

  return (
    <article className="max-w-3xl mx-auto px-4 sm:px-6 py-12" data-testid="blog-post">
      <Link to="/blog" className="flex items-center gap-1 text-zinc-500 hover:text-neon-green text-sm uppercase font-semibold mb-6"><ChevronLeft className="h-4 w-4" /> Blog</Link>
      <div className="flex items-center gap-3 mb-3">
        {post.category && <CategoryBadge cat={post.category} />}
        {post.reading_minutes && <span className="text-zinc-600 text-xs flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{post.reading_minutes} min de lecture</span>}
      </div>
      <h1 className="font-anton text-3xl sm:text-5xl uppercase text-white leading-tight mb-2">{post.title}</h1>
      <p className="text-zinc-500 text-sm mb-8">{fmtDate(post.created_at)} · STUFF CBD</p>
      {post.image_url && !hasContentImage && (
        <img src={post.image_url} alt={post.title} className="w-full border border-zinc-800 mb-8" data-testid="blog-hero-image" />
      )}
      <BlockRenderer content={post.content} />
      {(post.tags || []).length > 0 && (
        <div className="flex flex-wrap gap-2 mt-8" data-testid="blog-post-tags">
          {post.tags.map((t) => <TagBadge key={t} tag={t} />)}
        </div>
      )}
      <div className="mt-8 border-t border-zinc-800 pt-6">
        <ShareButtons url={`${window.location.origin}/api/share/blog/${post.slug}`} title={`${post.title} — Blog STUFF CBD 🌿`} label="Partager l'article" />
      </div>
      {post.job_posting && <JobApplyForm city={post.job_posting.city} jobTitle={post.job_posting.title} sourceSlug={post.slug} />}
      <div className="mt-10 border-2 border-neon-green bg-ink-surface p-6 text-center">
        <p className="font-anton text-xl uppercase text-white mb-3">{post.cta || "Envie de tester ? Livraison en moins de 2h 🚀"}</p>
        <Link to={post.cta_link || "/"} data-testid="blog-cta-link" className="inline-block bg-neon-green text-black font-anton uppercase px-8 py-3 glow-green">
          {post.job_posting ? "Devenir vendeur STUFF (ma boutique)" : post.cta_link === "/recrutement" ? "Postuler maintenant" : post.cta_link === "/club" ? "Découvrir le Club" : "Choisir ma ville"}
        </Link>
      </div>
    </article>
  );
}
